// Mock Data Generator for Access Project Management
import type {
  Client,
  Employee,
  Project,
  ProjectPhase,
  Task,
  TimeEntry,
  Expense,
  Milestone,
  ChangeRequest,
  WIPReport,
  UtilisationData,
  PortfolioSummary,
  IntegrationStatus,
  ResourceAllocation,
} from '@/types';

// UK Clients
export const mockClients: Client[] = [
  { id: 'c1', name: 'Deloitte UK', industry: 'Consulting', contactName: 'Sarah Mitchell', contactEmail: 'sarah.mitchell@deloitte.co.uk' },
  { id: 'c2', name: 'KPMG London', industry: 'Accounting', contactName: 'James Thompson', contactEmail: 'james.thompson@kpmg.co.uk' },
  { id: 'c3', name: 'Arup Group', industry: 'Engineering', contactName: 'Emma Richardson', contactEmail: 'emma.richardson@arup.com' },
  { id: 'c4', name: 'Clifford Chance', industry: 'Legal', contactName: 'Oliver Davies', contactEmail: 'oliver.davies@cliffordchance.com' },
  { id: 'c5', name: 'Accenture UK', industry: 'IT Services', contactName: 'Sophie Williams', contactEmail: 'sophie.williams@accenture.com' },
  { id: 'c6', name: 'Foster + Partners', industry: 'Architecture', contactName: 'Thomas Brown', contactEmail: 'thomas.brown@fosterandpartners.com' },
  { id: 'c7', name: 'PwC Birmingham', industry: 'Consulting', contactName: 'Charlotte Evans', contactEmail: 'charlotte.evans@pwc.com' },
  { id: 'c8', name: 'Mott MacDonald', industry: 'Engineering', contactName: 'William Taylor', contactEmail: 'william.taylor@mottmac.com' },
  { id: 'c9', name: 'Freshfields', industry: 'Legal', contactName: 'Amelia Wilson', contactEmail: 'amelia.wilson@freshfields.com' },
  { id: 'c10', name: 'Capgemini UK', industry: 'IT Services', contactName: 'George Harris', contactEmail: 'george.harris@capgemini.com' },
  { id: 'c11', name: 'Atkins Global', industry: 'Engineering', contactName: 'Isabella Martin', contactEmail: 'isabella.martin@atkinsglobal.com' },
  { id: 'c12', name: 'EY Manchester', industry: 'Accounting', contactName: 'Jack Robinson', contactEmail: 'jack.robinson@uk.ey.com' },
  { id: 'c13', name: 'Zaha Hadid Architects', industry: 'Architecture', contactName: 'Olivia Clark', contactEmail: 'olivia.clark@zaha-hadid.com' },
  { id: 'c14', name: 'BDO London', industry: 'Accounting', contactName: 'Harry Lewis', contactEmail: 'harry.lewis@bdo.co.uk' },
  { id: 'c15', name: 'Slaughter and May', industry: 'Legal', contactName: 'Lily Walker', contactEmail: 'lily.walker@slaughterandmay.com' },
];

// Employees with UK names and roles
export const mockEmployees: Employee[] = [
  { id: 'e1', name: 'Alice Johnson', email: 'alice.johnson@company.co.uk', role: 'Senior Project Manager', department: 'Delivery', costRate: 65, billRate: 150, skills: ['Project Management', 'Agile', 'Risk Management'], isAvailable: true },
  { id: 'e2', name: 'Ben Carter', email: 'ben.carter@company.co.uk', role: 'Project Manager', department: 'Delivery', costRate: 55, billRate: 125, skills: ['Project Management', 'Stakeholder Management'], isAvailable: true },
  { id: 'e3', name: 'Catherine Davies', email: 'catherine.davies@company.co.uk', role: 'Senior Consultant', department: 'Consulting', costRate: 70, billRate: 160, skills: ['Business Analysis', 'Strategy', 'Change Management'], isAvailable: true },
  { id: 'e4', name: 'David Smith', email: 'david.smith@company.co.uk', role: 'Lead Developer', department: 'Technology', costRate: 60, billRate: 140, skills: ['React', 'Node.js', 'AWS'], isAvailable: true },
  { id: 'e5', name: 'Emma Wilson', email: 'emma.wilson@company.co.uk', role: 'Senior Developer', department: 'Technology', costRate: 55, billRate: 130, skills: ['React', 'TypeScript', 'Azure'], isAvailable: true },
  { id: 'e6', name: 'Frank Brown', email: 'frank.brown@company.co.uk', role: 'Business Analyst', department: 'Consulting', costRate: 50, billRate: 115, skills: ['Requirements Gathering', 'Process Mapping', 'SQL'], isAvailable: true },
  { id: 'e7', name: 'Grace Taylor', email: 'grace.taylor@company.co.uk', role: 'UX Designer', department: 'Design', costRate: 48, billRate: 110, skills: ['Figma', 'User Research', 'Prototyping'], isAvailable: true },
  { id: 'e8', name: 'Henry Evans', email: 'henry.evans@company.co.uk', role: 'Data Analyst', department: 'Analytics', costRate: 45, billRate: 105, skills: ['Python', 'Power BI', 'SQL'], isAvailable: true },
  { id: 'e9', name: 'Isla Roberts', email: 'isla.roberts@company.co.uk', role: 'QA Lead', department: 'Quality', costRate: 50, billRate: 115, skills: ['Test Automation', 'Selenium', 'Agile Testing'], isAvailable: true },
  { id: 'e10', name: 'James Anderson', email: 'james.anderson@company.co.uk', role: 'DevOps Engineer', department: 'Technology', costRate: 58, billRate: 135, skills: ['Kubernetes', 'CI/CD', 'Terraform'], isAvailable: true },
  { id: 'e11', name: 'Katie White', email: 'katie.white@company.co.uk', role: 'Consultant', department: 'Consulting', costRate: 52, billRate: 120, skills: ['Business Analysis', 'Workshops', 'Documentation'], isAvailable: true },
  { id: 'e12', name: 'Liam Harris', email: 'liam.harris@company.co.uk', role: 'Developer', department: 'Technology', costRate: 45, billRate: 105, skills: ['JavaScript', 'React', 'Node.js'], isAvailable: true },
  { id: 'e13', name: 'Mia Thompson', email: 'mia.thompson@company.co.uk', role: 'Project Coordinator', department: 'Delivery', costRate: 35, billRate: 80, skills: ['Scheduling', 'Documentation', 'Stakeholder Communication'], isAvailable: true },
  { id: 'e14', name: 'Noah Martin', email: 'noah.martin@company.co.uk', role: 'Solutions Architect', department: 'Technology', costRate: 75, billRate: 170, skills: ['Architecture', 'Cloud', 'Security'], isAvailable: true },
  { id: 'e15', name: 'Olivia Jackson', email: 'olivia.jackson@company.co.uk', role: 'Change Manager', department: 'Consulting', costRate: 60, billRate: 140, skills: ['Change Management', 'Training', 'Communications'], isAvailable: true },
  { id: 'e16', name: 'Peter Clarke', email: 'peter.clarke@company.co.uk', role: 'Technical Lead', department: 'Technology', costRate: 65, billRate: 150, skills: ['.NET', 'Azure', 'Microservices'], isAvailable: true },
  { id: 'e17', name: 'Quinn Moore', email: 'quinn.moore@company.co.uk', role: 'Scrum Master', department: 'Delivery', costRate: 50, billRate: 115, skills: ['Scrum', 'Agile Coaching', 'Facilitation'], isAvailable: true },
  { id: 'e18', name: 'Rachel King', email: 'rachel.king@company.co.uk', role: 'Financial Analyst', department: 'Finance', costRate: 48, billRate: 110, skills: ['Financial Modelling', 'Excel', 'Reporting'], isAvailable: true },
  { id: 'e19', name: 'Samuel Wright', email: 'samuel.wright@company.co.uk', role: 'Security Specialist', department: 'Technology', costRate: 70, billRate: 160, skills: ['Cybersecurity', 'Penetration Testing', 'Compliance'], isAvailable: true },
  { id: 'e20', name: 'Tara Scott', email: 'tara.scott@company.co.uk', role: 'Product Owner', department: 'Delivery', costRate: 60, billRate: 140, skills: ['Product Management', 'Roadmapping', 'Prioritisation'], isAvailable: true },
];

// Generate realistic projects
export const mockProjects: Project[] = [
  // Green - On Track Projects
  {
    id: 'p1',
    name: 'Digital Transformation Programme',
    clientIds: 'c1', clientNames: 'Deloitte UK',
    projectManagerId: 'e1',
    type: 'time-and-materials',
    status: 'in-progress',
    health: 'green',
    startDate: '2024-01-15',
    endDate: '2025-06-30',
    description: 'Enterprise-wide digital transformation including cloud migration and process automation',
    budgetLabour: 850000,
    budgetExpense: 150000,
    budgetRevenue: 1200000,
    actualLabour: 425000,
    actualExpense: 68000,
    actualRevenue: 600000,
    budgetHours: 13000,
    actualHours: 6500,
    hoursVariance: -100,
    hoursVariancePercent: -1.5,
    totalBudget: 1000000,
    totalActual: 493000,
    variance: -7000,
    variancePercent: -1.4,
    marginPercent: 18.5,
    completionPercent: 48,
    revenueRecognitionMethod: 'percentage-of-completion',
    wipAmount: 93000,
    unbilledAmount: 45000,
    createdAt: '2023-12-01T10:00:00Z',
    updatedAt: '2025-01-15T14:30:00Z',
  },
  {
    id: 'p2',
    name: 'Financial Systems Integration',
    clientIds: 'c2', clientNames: 'KPMG London',
    projectManagerId: 'e2',
    type: 'fixed-price',
    status: 'in-progress',
    health: 'green',
    startDate: '2024-09-01',
    endDate: '2025-03-31',
    description: 'Integration of Access Financials with existing ERP systems',
    budgetLabour: 320000,
    budgetExpense: 30000,
    budgetRevenue: 450000,
    actualLabour: 180000,
    actualExpense: 15000,
    actualRevenue: 225000,
    budgetHours: 5800,
    actualHours: 3200,
    hoursVariance: -80,
    hoursVariancePercent: -1.4,
    totalBudget: 350000,
    totalActual: 195000,
    variance: -5000,
    variancePercent: -1.4,
    marginPercent: 28.3,
    completionPercent: 55,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 30000,
    unbilledAmount: 0,
    createdAt: '2024-08-01T09:00:00Z',
    updatedAt: '2025-01-14T16:20:00Z',
  },
  {
    id: 'p3',
    name: 'Infrastructure Modernisation',
    clientIds: 'c3', clientNames: 'Arup Group',
    projectManagerId: 'e1',
    type: 'milestone-based',
    status: 'in-progress',
    health: 'green',
    startDate: '2024-06-01',
    endDate: '2025-05-31',
    description: 'Cloud infrastructure setup and migration to Azure',
    budgetLabour: 580000,
    budgetExpense: 120000,
    budgetRevenue: 900000,
    actualLabour: 310000,
    actualExpense: 58000,
    actualRevenue: 450000,
    budgetHours: 10000,
    actualHours: 5200,
    hoursVariance: -260,
    hoursVariancePercent: -2.6,
    totalBudget: 700000,
    totalActual: 368000,
    variance: -18000,
    variancePercent: -2.6,
    marginPercent: 27.8,
    completionPercent: 52,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 82000,
    unbilledAmount: 0,
    createdAt: '2024-05-01T11:00:00Z',
    updatedAt: '2025-01-15T10:15:00Z',
  },
  
  // Amber - At Risk Projects
  {
    id: 'p4',
    name: 'Legal Case Management System',
    clientIds: 'c4', clientNames: 'Clifford Chance',
    projectManagerId: 'e2',
    type: 'fixed-price',
    status: 'in-progress',
    health: 'amber',
    startDate: '2024-10-01',
    endDate: '2025-04-30',
    description: 'Custom case management platform for legal practice',
    budgetLabour: 280000,
    budgetExpense: 20000,
    budgetRevenue: 380000,
    actualLabour: 195000,
    actualExpense: 18000,
    actualRevenue: 190000,
    budgetHours: 5100,
    actualHours: 3550,
    hoursVariance: 390,
    hoursVariancePercent: 7.7,
    totalBudget: 300000,
    totalActual: 213000,
    variance: 23000,
    variancePercent: 7.7,
    marginPercent: 15.8,
    completionPercent: 58,
    revenueRecognitionMethod: 'percentage-of-completion',
    wipAmount: 23000,
    unbilledAmount: 23000,
    createdAt: '2024-09-01T10:00:00Z',
    updatedAt: '2025-01-15T13:45:00Z',
  },
  {
    id: 'p5',
    name: 'Mobile App Development',
    clientIds: 'c5', clientNames: 'Accenture UK',
    projectManagerId: 'e20',
    type: 'time-and-materials',
    status: 'in-progress',
    health: 'amber',
    startDate: '2024-08-15',
    endDate: '2025-02-28',
    description: 'iOS and Android mobile application for field services',
    budgetLabour: 420000,
    budgetExpense: 30000,
    budgetRevenue: 550000,
    actualLabour: 310000,
    actualExpense: 28000,
    actualRevenue: 385000,
    budgetHours: 7600,
    actualHours: 5640,
    hoursVariance: 640,
    hoursVariancePercent: 8.4,
    totalBudget: 450000,
    totalActual: 338000,
    variance: 38000,
    variancePercent: 8.4,
    marginPercent: 16.5,
    completionPercent: 72,
    revenueRecognitionMethod: 'time-based',
    wipAmount: -47000,
    unbilledAmount: 0,
    createdAt: '2024-07-15T09:30:00Z',
    updatedAt: '2025-01-15T11:20:00Z',
  },
  {
    id: 'p6',
    name: 'Architectural Design Platform',
    clientIds: 'c6', clientNames: 'Foster + Partners',
    projectManagerId: 'e3',
    type: 'milestone-based',
    status: 'in-progress',
    health: 'amber',
    startDate: '2024-07-01',
    endDate: '2025-06-30',
    description: 'Collaborative design platform with 3D visualization',
    budgetLabour: 680000,
    budgetExpense: 70000,
    budgetRevenue: 950000,
    actualLabour: 425000,
    actualExpense: 52000,
    actualRevenue: 475000,
    budgetHours: 11700,
    actualHours: 7310,
    hoursVariance: 420,
    hoursVariancePercent: 3.6,
    totalBudget: 750000,
    totalActual: 477000,
    variance: 27000,
    variancePercent: 3.6,
    marginPercent: 21.3,
    completionPercent: 58,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 2000,
    unbilledAmount: 0,
    createdAt: '2024-06-01T10:00:00Z',
    updatedAt: '2025-01-14T15:30:00Z',
  },
  
  // Red - Critical Projects
  {
    id: 'p7',
    name: 'ERP Implementation',
    clientIds: 'c7', clientNames: 'PwC Birmingham',
    projectManagerId: 'e1',
    type: 'fixed-price',
    status: 'in-progress',
    health: 'red',
    startDate: '2024-05-01',
    endDate: '2025-01-31',
    description: 'Full ERP system implementation and data migration',
    budgetLabour: 520000,
    budgetExpense: 80000,
    budgetRevenue: 750000,
    actualLabour: 615000,
    actualExpense: 92000,
    actualRevenue: 525000,
    budgetHours: 9500,
    actualHours: 11190,
    hoursVariance: 1690,
    hoursVariancePercent: 17.8,
    totalBudget: 600000,
    totalActual: 707000,
    variance: 107000,
    variancePercent: 17.8,
    marginPercent: 5.7,
    completionPercent: 85,
    revenueRecognitionMethod: 'percentage-of-completion',
    wipAmount: 182000,
    unbilledAmount: 182000,
    createdAt: '2024-04-01T09:00:00Z',
    updatedAt: '2025-01-15T16:45:00Z',
  },
  {
    id: 'p8',
    name: 'Data Centre Migration',
    clientIds: 'c8', clientNames: 'Mott MacDonald',
    projectManagerId: 'e2',
    type: 'time-and-materials',
    status: 'in-progress',
    health: 'red',
    startDate: '2024-09-01',
    endDate: '2025-02-28',
    description: 'Physical data centre migration with zero downtime requirement',
    budgetLabour: 380000,
    budgetExpense: 120000,
    budgetRevenue: 650000,
    actualLabour: 445000,
    actualExpense: 138000,
    actualRevenue: 520000,
    budgetHours: 6900,
    actualHours: 8050,
    hoursVariance: 1150,
    hoursVariancePercent: 16.6,
    totalBudget: 500000,
    totalActual: 583000,
    variance: 83000,
    variancePercent: 16.6,
    marginPercent: 10.3,
    completionPercent: 78,
    revenueRecognitionMethod: 'time-based',
    wipAmount: 63000,
    unbilledAmount: 63000,
    createdAt: '2024-08-01T10:00:00Z',
    updatedAt: '2025-01-15T12:30:00Z',
  },
  
  // More varied projects
  {
    id: 'p9',
    name: 'Compliance Audit System',
    clientIds: 'c9', clientNames: 'Freshfields',
    projectManagerId: 'e3',
    type: 'retainer',
    status: 'in-progress',
    health: 'green',
    startDate: '2024-01-01',
    endDate: '2025-12-31',
    description: 'Ongoing compliance monitoring and audit support',
    budgetLabour: 240000,
    budgetExpense: 10000,
    budgetRevenue: 300000,
    actualLabour: 120000,
    actualExpense: 5000,
    actualRevenue: 150000,
    budgetHours: 4350,
    actualHours: 2175,
    hoursVariance: 0,
    hoursVariancePercent: 0,
    totalBudget: 250000,
    totalActual: 125000,
    variance: 0,
    variancePercent: 0,
    marginPercent: 20.0,
    completionPercent: 50,
    revenueRecognitionMethod: 'time-based',
    wipAmount: 0,
    unbilledAmount: 0,
    createdAt: '2023-12-01T09:00:00Z',
    updatedAt: '2025-01-15T09:00:00Z',
  },
  {
    id: 'p10',
    name: 'Cloud Security Assessment',
    clientIds: 'c10', clientNames: 'Capgemini UK',
    projectManagerId: 'e2',
    type: 'fixed-price',
    status: 'in-progress',
    health: 'green',
    startDate: '2024-11-01',
    endDate: '2025-03-31',
    description: 'Comprehensive security audit and penetration testing',
    budgetLabour: 180000,
    budgetExpense: 20000,
    budgetRevenue: 250000,
    actualLabour: 95000,
    actualExpense: 8000,
    actualRevenue: 125000,
    budgetHours: 3200,
    actualHours: 1680,
    hoursVariance: -50,
    hoursVariancePercent: -1.5,
    totalBudget: 200000,
    totalActual: 103000,
    variance: -7000,
    variancePercent: -3.5,
    marginPercent: 25.2,
    completionPercent: 48,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 22000,
    unbilledAmount: 0,
    createdAt: '2024-10-01T10:00:00Z',
    updatedAt: '2025-01-14T14:15:00Z',
  },
  {
    id: 'p11',
    name: 'Engineering Simulation Platform',
    clientIds: 'c11', clientNames: 'Atkins Global',
    projectManagerId: 'e1',
    type: 'milestone-based',
    status: 'in-progress',
    health: 'amber',
    startDate: '2024-04-01',
    endDate: '2025-09-30',
    description: 'High-performance computing platform for engineering simulations',
    budgetLabour: 920000,
    budgetExpense: 180000,
    budgetRevenue: 1400000,
    actualLabour: 580000,
    actualExpense: 115000,
    actualRevenue: 840000,
    budgetHours: 15800,
    actualHours: 9950,
    hoursVariance: 650,
    hoursVariancePercent: 4.1,
    totalBudget: 1100000,
    totalActual: 695000,
    variance: 45000,
    variancePercent: 4.1,
    marginPercent: 23.8,
    completionPercent: 60,
    revenueRecognitionMethod: 'milestone',
    wipAmount: -145000,
    unbilledAmount: 0,
    createdAt: '2024-03-01T09:00:00Z',
    updatedAt: '2025-01-15T15:20:00Z',
  },
  {
    id: 'p12',
    name: 'Tax Compliance Portal',
    clientIds: 'c12', clientNames: 'EY Manchester',
    projectManagerId: 'e20',
    type: 'fixed-price',
    status: 'in-progress',
    health: 'green',
    startDate: '2024-10-15',
    endDate: '2025-04-15',
    description: 'Self-service tax compliance portal with MTD integration',
    budgetLabour: 260000,
    budgetExpense: 15000,
    budgetRevenue: 350000,
    actualLabour: 145000,
    actualExpense: 8000,
    actualRevenue: 175000,
    budgetHours: 4700,
    actualHours: 2630,
    hoursVariance: -120,
    hoursVariancePercent: -2.5,
    totalBudget: 275000,
    totalActual: 153000,
    variance: -7000,
    variancePercent: -2.5,
    marginPercent: 26.3,
    completionPercent: 52,
    revenueRecognitionMethod: 'percentage-of-completion',
    wipAmount: 22000,
    unbilledAmount: 0,
    createdAt: '2024-09-15T10:00:00Z',
    updatedAt: '2025-01-15T11:45:00Z',
  },
  {
    id: 'p13',
    name: 'Smart Building IoT System',
    clientIds: 'c13', clientNames: 'Zaha Hadid Architects',
    projectManagerId: 'e3',
    type: 'time-and-materials',
    status: 'in-progress',
    health: 'green',
    startDate: '2024-08-01',
    endDate: '2025-07-31',
    description: 'IoT sensor network and building management system',
    budgetLabour: 480000,
    budgetExpense: 220000,
    budgetRevenue: 900000,
    actualLabour: 240000,
    actualExpense: 105000,
    actualRevenue: 450000,
    budgetHours: 8200,
    actualHours: 4120,
    hoursVariance: -60,
    hoursVariancePercent: -0.7,
    totalBudget: 700000,
    totalActual: 345000,
    variance: -5000,
    variancePercent: -0.7,
    marginPercent: 28.3,
    completionPercent: 49,
    revenueRecognitionMethod: 'time-based',
    wipAmount: 105000,
    unbilledAmount: 55000,
    createdAt: '2024-07-01T09:00:00Z',
    updatedAt: '2025-01-15T10:30:00Z',
  },
  {
    id: 'p14',
    name: 'Financial Reporting Automation',
    clientIds: 'c14', clientNames: 'BDO London',
    projectManagerId: 'e2',
    type: 'fixed-price',
    status: 'in-progress',
    health: 'amber',
    startDate: '2024-09-15',
    endDate: '2025-03-15',
    description: 'Automated financial reporting with Power BI dashboards',
    budgetLabour: 220000,
    budgetExpense: 30000,
    budgetRevenue: 320000,
    actualLabour: 165000,
    actualExpense: 25000,
    actualRevenue: 192000,
    budgetHours: 4000,
    actualHours: 3000,
    hoursVariance: 240,
    hoursVariancePercent: 6.0,
    totalBudget: 250000,
    totalActual: 190000,
    variance: 15000,
    variancePercent: 6.0,
    marginPercent: 18.8,
    completionPercent: 68,
    revenueRecognitionMethod: 'milestone',
    wipAmount: -2000,
    unbilledAmount: 0,
    createdAt: '2024-08-15T10:00:00Z',
    updatedAt: '2025-01-14T16:00:00Z',
  },
  {
    id: 'p15',
    name: 'Contract Management System',
    clientIds: 'c15', clientNames: 'Slaughter and May',
    projectManagerId: 'e1',
    type: 'milestone-based',
    status: 'in-progress',
    health: 'green',
    startDate: '2024-07-15',
    endDate: '2025-05-15',
    description: 'Enterprise contract lifecycle management platform',
    budgetLabour: 540000,
    budgetExpense: 60000,
    budgetRevenue: 780000,
    actualLabour: 310000,
    actualExpense: 32000,
    actualRevenue: 390000,
    budgetHours: 9800,
    actualHours: 5640,
    hoursVariance: -130,
    hoursVariancePercent: -1.3,
    totalBudget: 600000,
    totalActual: 342000,
    variance: -8000,
    variancePercent: -1.3,
    marginPercent: 25.6,
    completionPercent: 55,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 48000,
    unbilledAmount: 0,
    createdAt: '2024-06-15T09:00:00Z',
    updatedAt: '2025-01-15T14:00:00Z',
  },
  
  // Completed projects
  {
    id: 'p16',
    name: 'Website Redesign',
    clientIds: 'c1', clientNames: 'Deloitte UK',
    projectManagerId: 'e20',
    type: 'fixed-price',
    status: 'completed',
    health: 'green',
    startDate: '2024-06-01',
    endDate: '2024-12-31',
    description: 'Corporate website redesign and CMS migration',
    budgetLabour: 180000,
    budgetExpense: 20000,
    budgetRevenue: 250000,
    actualLabour: 175000,
    actualExpense: 18000,
    actualRevenue: 250000,
    budgetHours: 3270,
    actualHours: 3180,
    hoursVariance: -115,
    hoursVariancePercent: -3.5,
    totalBudget: 200000,
    totalActual: 193000,
    variance: -7000,
    variancePercent: -3.5,
    marginPercent: 22.8,
    completionPercent: 100,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 0,
    unbilledAmount: 0,
    createdAt: '2024-05-01T09:00:00Z',
    updatedAt: '2024-12-31T17:00:00Z',
  },
  {
    id: 'p17',
    name: 'Cybersecurity Training Programme',
    clientIds: 'c5', clientNames: 'Accenture UK',
    projectManagerId: 'e3',
    type: 'time-and-materials',
    status: 'completed',
    health: 'green',
    startDate: '2024-08-01',
    endDate: '2024-12-15',
    description: 'Organisation-wide security awareness training',
    budgetLabour: 95000,
    budgetExpense: 5000,
    budgetRevenue: 120000,
    actualLabour: 92000,
    actualExpense: 4500,
    actualRevenue: 120000,
    budgetHours: 1720,
    actualHours: 1670,
    hoursVariance: -50,
    hoursVariancePercent: -2.9,
    totalBudget: 100000,
    totalActual: 96500,
    variance: -3500,
    variancePercent: -3.5,
    marginPercent: 19.6,
    completionPercent: 100,
    revenueRecognitionMethod: 'time-based',
    wipAmount: 0,
    unbilledAmount: 0,
    createdAt: '2024-07-01T09:00:00Z',
    updatedAt: '2024-12-15T17:00:00Z',
  },
  
  // Not started projects
  {
    id: 'p18',
    name: 'AI-Powered Analytics Platform',
    clientIds: 'c2', clientNames: 'KPMG London',
    projectManagerId: 'e1',
    type: 'milestone-based',
    status: 'not-started',
    health: 'green',
    startDate: '2025-02-01',
    endDate: '2025-11-30',
    description: 'Machine learning analytics platform for financial forecasting',
    budgetLabour: 780000,
    budgetExpense: 120000,
    budgetRevenue: 1200000,
    actualLabour: 0,
    actualExpense: 0,
    actualRevenue: 0,
    budgetHours: 14200,
    actualHours: 0,
    hoursVariance: 0,
    hoursVariancePercent: 0,
    totalBudget: 900000,
    totalActual: 0,
    variance: 0,
    variancePercent: 0,
    marginPercent: 33.3,
    completionPercent: 0,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 0,
    unbilledAmount: 0,
    createdAt: '2025-01-10T10:00:00Z',
    updatedAt: '2025-01-10T10:00:00Z',
  },
];

// Helper function to get project by ID
export const getProjectById = (id: string): Project | undefined => {
  return mockProjects.find(p => p.id === id);
};

// Helper function to get projects by client (now supports multiple clients per project)
export const getProjectsByClient = (clientId: string): Project[] => {
  return mockProjects.filter(p => p.clientIds?.split(',').includes(clientId));
};

// Helper function to get projects by PM
export const getProjectsByPM = (pmId: string): Project[] => {
  return mockProjects.filter(p => p.projectManagerId === pmId);
};

// Helper function to get client by ID
export const getClientById = (id: string): Client | undefined => {
  return mockClients.find(c => c.id === id);
};

// Helper function to get employee by ID
export const getEmployeeById = (id: string): Employee | undefined => {
  return mockEmployees.find(e => e.id === id);
};

// Helper function to get allocations by project
export const getAllocationsByProject = (projectId: string): ResourceAllocation[] => {
  return mockResourceAllocations.filter(ra => ra.projectId === projectId);
};

// Resource Allocations
export const mockResourceAllocations: ResourceAllocation[] = [
  // Project 1 - Digital Transformation Programme
  { id: 'ra1', projectId: 'p1', employeeId: 'e1', allocationPercent: 80, startDate: '2024-01-15', endDate: '2025-06-30', role: 'Project Manager', status: 'confirmed', conflictLevel: 'none' },
  { id: 'ra2', projectId: 'p1', employeeId: 'e4', allocationPercent: 100, startDate: '2024-01-15', endDate: '2025-06-30', role: 'Lead Developer', status: 'confirmed', conflictLevel: 'none' },
  { id: 'ra3', projectId: 'p1', employeeId: 'e5', allocationPercent: 100, startDate: '2024-01-15', endDate: '2025-06-30', role: 'Senior Developer', status: 'confirmed', conflictLevel: 'none' },
  { id: 'ra4', projectId: 'p1', employeeId: 'e3', allocationPercent: 60, startDate: '2024-01-15', endDate: '2025-03-31', role: 'Senior Consultant', status: 'confirmed', conflictLevel: 'none' },
  { id: 'ra5', projectId: 'p1', employeeId: 'e10', allocationPercent: 50, startDate: '2024-02-01', endDate: '2025-06-30', role: 'DevOps Engineer', status: 'confirmed', conflictLevel: 'none' },
  
  // Project 2 - Financial Systems Integration
  { id: 'ra6', projectId: 'p2', employeeId: 'e2', allocationPercent: 70, startDate: '2024-09-01', endDate: '2025-03-31', role: 'Project Manager', status: 'confirmed', conflictLevel: 'none' },
  { id: 'ra7', projectId: 'p2', employeeId: 'e6', allocationPercent: 100, startDate: '2024-09-01', endDate: '2025-03-31', role: 'Business Analyst', status: 'confirmed', conflictLevel: 'none' },
  { id: 'ra8', projectId: 'p2', employeeId: 'e12', allocationPercent: 100, startDate: '2024-09-01', endDate: '2025-03-31', role: 'Developer', status: 'confirmed', conflictLevel: 'none' },
  { id: 'ra9', projectId: 'p2', employeeId: 'e18', allocationPercent: 50, startDate: '2024-09-01', endDate: '2025-03-31', role: 'Financial Analyst', status: 'confirmed', conflictLevel: 'none' },
  
  // Project 3 - Infrastructure Modernisation
  { id: 'ra10', projectId: 'p3', employeeId: 'e1', allocationPercent: 20, startDate: '2024-06-01', endDate: '2025-05-31', role: 'Senior Project Manager', status: 'confirmed', conflictLevel: 'warning' },
  { id: 'ra11', projectId: 'p3', employeeId: 'e14', allocationPercent: 80, startDate: '2024-06-01', endDate: '2025-05-31', role: 'Solutions Architect', status: 'confirmed', conflictLevel: 'none' },
  { id: 'ra12', projectId: 'p3', employeeId: 'e10', allocationPercent: 50, startDate: '2024-06-01', endDate: '2025-05-31', role: 'DevOps Engineer', status: 'confirmed', conflictLevel: 'critical' },
  { id: 'ra13', projectId: 'p3', employeeId: 'e19', allocationPercent: 60, startDate: '2024-06-01', endDate: '2025-05-31', role: 'Security Specialist', status: 'confirmed', conflictLevel: 'none' },
  
  // Project 4 - Legal Case Management System
  { id: 'ra14', projectId: 'p4', employeeId: 'e2', allocationPercent: 30, startDate: '2024-10-01', endDate: '2025-04-30', role: 'Project Manager', status: 'confirmed', conflictLevel: 'warning' },
  { id: 'ra15', projectId: 'p4', employeeId: 'e11', allocationPercent: 100, startDate: '2024-10-01', endDate: '2025-04-30', role: 'Consultant', status: 'confirmed', conflictLevel: 'none' },
  { id: 'ra16', projectId: 'p4', employeeId: 'e12', allocationPercent: 80, startDate: '2024-10-01', endDate: '2025-04-30', role: 'Developer', status: 'tentative', conflictLevel: 'warning' },
  { id: 'ra17', projectId: 'p4', employeeId: 'e7', allocationPercent: 40, startDate: '2024-10-01', endDate: '2025-02-28', role: 'UX Designer', status: 'confirmed', conflictLevel: 'none' },
  
  // Project 5 - Mobile App Development
  { id: 'ra18', projectId: 'p5', employeeId: 'e20', allocationPercent: 60, startDate: '2024-08-15', endDate: '2025-02-28', role: 'Product Owner', status: 'confirmed', conflictLevel: 'none' },
  { id: 'ra19', projectId: 'p5', employeeId: 'e4', allocationPercent: 100, startDate: '2024-08-15', endDate: '2025-02-28', role: 'Lead Developer', status: 'tentative', conflictLevel: 'critical' },
  { id: 'ra20', projectId: 'p5', employeeId: 'e5', allocationPercent: 100, startDate: '2024-08-15', endDate: '2025-02-28', role: 'Senior Developer', status: 'tentative', conflictLevel: 'critical' },
  { id: 'ra21', projectId: 'p5', employeeId: 'e7', allocationPercent: 60, startDate: '2024-08-15', endDate: '2025-02-28', role: 'UX Designer', status: 'confirmed', conflictLevel: 'warning' },
  { id: 'ra22', projectId: 'p5', employeeId: 'e9', allocationPercent: 50, startDate: '2024-08-15', endDate: '2025-02-28', role: 'QA Lead', status: 'confirmed', conflictLevel: 'none' },
];

// WIP Reports
export const mockWIPReports: WIPReport[] = mockProjects.map(project => {
  // Now uses clientNames directly from project (supports multiple clients)
  const costsIncurred = project.totalActual;
  const profitEarned = project.actualRevenue - costsIncurred;
  const amountBilled = project.actualRevenue - project.unbilledAmount;
  const wipAmount = project.wipAmount;
  const overBilling = wipAmount < 0 ? Math.abs(wipAmount) : 0;
  const underBilling = wipAmount > 0 ? wipAmount : 0;
  
  // Calculate aging based on WIP amount and project status
  let agingDays = 0;
  if (wipAmount > 0) {
    // Under-billed projects - aging based on how long costs have been sitting
    agingDays = Math.floor(Math.random() * 90) + 1;
  }
  
  const readyToBill = wipAmount > 10000 && project.status === 'in-progress';
  
  return {
    projectId: project.id,
    projectName: project.name,
    clientName: project.clientNames || 'Unknown Client',
    costsIncurred,
    profitEarned,
    amountBilled,
    wipAmount,
    agingDays,
    readyToBill,
    overBilling,
    underBilling,
  };
});

// Utilisation Data
export const mockUtilisationData: UtilisationData[] = mockEmployees.map(employee => {
  // Calculate total allocation from resource allocations
  const allocations = mockResourceAllocations.filter(ra => ra.employeeId === employee.id);
  const totalAllocation = allocations.reduce((sum, ra) => sum + ra.allocationPercent, 0);
  
  // Assume 40 hours per week, 52 weeks per year = 2080 hours per year
  const availableHours = 2080;
  const billableHours = (totalAllocation / 100) * availableHours;
  const utilisationPercent = (billableHours / availableHours) * 100;
  const targetPercent = 75; // Target 75% utilisation
  
  let status: 'under' | 'target' | 'over' = 'target';
  if (utilisationPercent < targetPercent - 10) status = 'under';
  else if (utilisationPercent > targetPercent + 10) status = 'over';
  
  return {
    employeeId: employee.id,
    employeeName: employee.name,
    role: employee.role,
    department: employee.department,
    billableHours,
    availableHours,
    utilisationPercent,
    targetPercent,
    status,
  };
});

// Portfolio Summary
export const mockPortfolioSummary: PortfolioSummary = {
  totalProjects: mockProjects.length,
  activeProjects: mockProjects.filter(p => p.status === 'in-progress').length,
  portfolioValue: mockProjects.reduce((sum, p) => sum + p.budgetRevenue, 0),
  totalWIP: mockProjects.reduce((sum, p) => sum + p.wipAmount, 0),
  avgMarginPercent: mockProjects.reduce((sum, p) => sum + p.marginPercent, 0) / mockProjects.length,
  avgUtilisationPercent: mockUtilisationData.reduce((sum, u) => sum + u.utilisationPercent, 0) / mockUtilisationData.length,
  projectsByStatus: {
    'not-started': mockProjects.filter(p => p.status === 'not-started').length,
    'in-progress': mockProjects.filter(p => p.status === 'in-progress').length,
    'on-hold': mockProjects.filter(p => p.status === 'on-hold').length,
    'completed': mockProjects.filter(p => p.status === 'completed').length,
    'cancelled': mockProjects.filter(p => p.status === 'cancelled').length,
  },
  projectsByHealth: {
    'green': mockProjects.filter(p => p.health === 'green').length,
    'amber': mockProjects.filter(p => p.health === 'amber').length,
    'red': mockProjects.filter(p => p.health === 'red').length,
  },
};

// Integration Status
export const mockIntegrationStatus: IntegrationStatus[] = [
  { service: 'access-dimensions', status: 'connected', lastSync: '2025-01-15T16:00:00Z' },
  { service: 'access-financials', status: 'syncing', lastSync: '2025-01-15T15:45:00Z' },
  { service: 'timesheet-system', status: 'connected', lastSync: '2025-01-15T16:15:00Z' },
  { service: 'resource-scheduling', status: 'error', lastSync: '2025-01-15T14:30:00Z', errorMessage: 'Authentication token expired' },
];

// Mock Tasks
export const mockTasks: Task[] = [
  // Project 1 - Digital Transformation Programme
  { id: 'task1', projectId: 'p1', name: 'Requirements Gathering', description: 'Gather and document requirements', assigneeId: 'e3', status: 'completed', priority: 'high', dueDate: '2024-02-15', estimatedHours: 80, actualHours: 85 },
  { id: 'task2', projectId: 'p1', name: 'Architecture Design', description: 'Design system architecture', assigneeId: 'e14', status: 'completed', priority: 'high', dueDate: '2024-03-01', estimatedHours: 120, actualHours: 115 },
  { id: 'task3', projectId: 'p1', name: 'Frontend Development', description: 'Build React frontend', assigneeId: 'e4', status: 'in-progress', priority: 'high', dueDate: '2025-03-31', estimatedHours: 400, actualHours: 280 },
  { id: 'task4', projectId: 'p1', name: 'Backend API Development', description: 'Build REST APIs', assigneeId: 'e5', status: 'in-progress', priority: 'high', dueDate: '2025-03-31', estimatedHours: 350, actualHours: 245 },
  { id: 'task5', projectId: 'p1', name: 'Cloud Infrastructure Setup', description: 'Configure Azure resources', assigneeId: 'e10', status: 'in-progress', priority: 'medium', dueDate: '2025-02-28', estimatedHours: 160, actualHours: 120 },
  
  // Project 2 - Financial Systems Integration
  { id: 'task6', projectId: 'p2', name: 'System Analysis', description: 'Analyze existing systems', assigneeId: 'e6', status: 'completed', priority: 'high', dueDate: '2024-10-15', estimatedHours: 60, actualHours: 65 },
  { id: 'task7', projectId: 'p2', name: 'Integration Development', description: 'Build integration layer', assigneeId: 'e12', status: 'in-progress', priority: 'high', dueDate: '2025-02-28', estimatedHours: 200, actualHours: 145 },
  { id: 'task8', projectId: 'p2', name: 'Data Migration', description: 'Migrate historical data', assigneeId: 'e18', status: 'in-progress', priority: 'medium', dueDate: '2025-03-15', estimatedHours: 80, actualHours: 45 },
  { id: 'task9', projectId: 'p2', name: 'User Acceptance Testing', description: 'Conduct UAT with stakeholders', assigneeId: 'e2', status: 'todo', priority: 'high', dueDate: '2025-03-20', estimatedHours: 40, actualHours: 0 },
  
  // Project 4 - Legal Case Management System (Amber - has overdue tasks)
  { id: 'task10', projectId: 'p4', name: 'Requirements Workshop', description: 'Workshop with legal team', assigneeId: 'e11', status: 'completed', priority: 'high', dueDate: '2024-10-15', estimatedHours: 24, actualHours: 28 },
  { id: 'task11', projectId: 'p4', name: 'Database Design', description: 'Design case management database', assigneeId: 'e12', status: 'completed', priority: 'high', dueDate: '2024-11-30', estimatedHours: 60, actualHours: 72 },
  { id: 'task12', projectId: 'p4', name: 'Case Workflow Implementation', description: 'Build case workflow engine', assigneeId: 'e12', status: 'in-progress', priority: 'critical', dueDate: '2025-01-10', estimatedHours: 120, actualHours: 95 },
  { id: 'task13', projectId: 'p4', name: 'Document Management Module', description: 'Build document storage', assigneeId: 'e12', status: 'in-progress', priority: 'high', dueDate: '2025-01-05', estimatedHours: 80, actualHours: 65 },
  { id: 'task14', projectId: 'p4', name: 'Security Audit', description: 'Conduct security review', assigneeId: 'e19', status: 'todo', priority: 'critical', dueDate: '2025-01-12', estimatedHours: 40, actualHours: 0 },
  
  // Project 7 - ERP Implementation (Red - multiple overdue tasks)
  { id: 'task15', projectId: 'p7', name: 'Data Cleansing', description: 'Clean legacy data', assigneeId: 'e6', status: 'in-progress', priority: 'critical', dueDate: '2024-12-15', estimatedHours: 200, actualHours: 245 },
  { id: 'task16', projectId: 'p7', name: 'Module Configuration', description: 'Configure ERP modules', assigneeId: 'e11', status: 'in-progress', priority: 'critical', dueDate: '2024-12-20', estimatedHours: 180, actualHours: 215 },
  { id: 'task17', projectId: 'p7', name: 'User Training', description: 'Train end users', assigneeId: 'e15', status: 'in-progress', priority: 'high', dueDate: '2025-01-10', estimatedHours: 60, actualHours: 45 },
  { id: 'task18', projectId: 'p7', name: 'Go-Live Preparation', description: 'Prepare for go-live', assigneeId: 'e1', status: 'todo', priority: 'critical', dueDate: '2025-01-20', estimatedHours: 80, actualHours: 0 },
  
  // Project 5 - Mobile App Development
  { id: 'task19', projectId: 'p5', name: 'UI/UX Design', description: 'Design mobile app interface', assigneeId: 'e7', status: 'completed', priority: 'high', dueDate: '2024-10-15', estimatedHours: 100, actualHours: 110 },
  { id: 'task20', projectId: 'p5', name: 'iOS Development', description: 'Build iOS app', assigneeId: 'e4', status: 'in-progress', priority: 'high', dueDate: '2025-02-15', estimatedHours: 200, actualHours: 165 },
  { id: 'task21', projectId: 'p5', name: 'Android Development', description: 'Build Android app', assigneeId: 'e5', status: 'in-progress', priority: 'high', dueDate: '2025-02-15', estimatedHours: 200, actualHours: 170 },
  { id: 'task22', projectId: 'p5', name: 'API Integration', description: 'Integrate with backend APIs', assigneeId: 'e4', status: 'in-progress', priority: 'medium', dueDate: '2025-02-20', estimatedHours: 80, actualHours: 55 },
  { id: 'task23', projectId: 'p5', name: 'App Store Submission', description: 'Submit to app stores', assigneeId: 'e20', status: 'todo', priority: 'medium', dueDate: '2025-02-25', estimatedHours: 16, actualHours: 0 },
];

// Mock Milestones
export const mockMilestones: Milestone[] = [
  // Project 1 - Digital Transformation Programme
  { id: 'ms1', projectId: 'p1', name: 'Requirements Sign-off', dueDate: '2024-02-28', completedDate: '2024-02-25', status: 'completed', paymentAmount: 120000, paymentReceived: true },
  { id: 'ms2', projectId: 'p1', name: 'Architecture Approval', dueDate: '2024-03-31', completedDate: '2024-03-28', status: 'completed', paymentAmount: 150000, paymentReceived: true },
  { id: 'ms3', projectId: 'p1', name: 'MVP Release', dueDate: '2024-09-30', completedDate: '2024-10-05', status: 'completed', paymentAmount: 200000, paymentReceived: true },
  { id: 'ms4', projectId: 'p1', name: 'Beta Release', dueDate: '2025-03-31', status: 'pending', paymentAmount: 250000, paymentReceived: false },
  { id: 'ms5', projectId: 'p1', name: 'Production Go-Live', dueDate: '2025-06-30', status: 'pending', paymentAmount: 280000, paymentReceived: false },
  
  // Project 2 - Financial Systems Integration
  { id: 'ms6', projectId: 'p2', name: 'Analysis Complete', dueDate: '2024-10-31', completedDate: '2024-10-28', status: 'completed', paymentAmount: 90000, paymentReceived: true },
  { id: 'ms7', projectId: 'p2', name: 'Integration Testing', dueDate: '2025-02-28', status: 'pending', paymentAmount: 135000, paymentReceived: false },
  { id: 'ms8', projectId: 'p2', name: 'Go-Live', dueDate: '2025-03-31', status: 'pending', paymentAmount: 225000, paymentReceived: false },
  
  // Project 3 - Infrastructure Modernisation
  { id: 'ms9', projectId: 'p3', name: 'Infrastructure Design', dueDate: '2024-07-31', completedDate: '2024-07-28', status: 'completed', paymentAmount: 180000, paymentReceived: true },
  { id: 'ms10', projectId: 'p3', name: 'Phase 1 Migration', dueDate: '2024-11-30', completedDate: '2024-11-25', status: 'completed', paymentAmount: 270000, paymentReceived: true },
  { id: 'ms11', projectId: 'p3', name: 'Phase 2 Migration', dueDate: '2025-03-31', status: 'pending', paymentAmount: 270000, paymentReceived: false },
  { id: 'ms12', projectId: 'p3', name: 'Final Cutover', dueDate: '2025-05-31', status: 'pending', paymentAmount: 180000, paymentReceived: false },
  
  // Project 4 - Legal Case Management System (Amber - has overdue milestone)
  { id: 'ms13', projectId: 'p4', name: 'Requirements Approval', dueDate: '2024-11-15', completedDate: '2024-11-18', status: 'completed', paymentAmount: 76000, paymentReceived: true },
  { id: 'ms14', projectId: 'p4', name: 'Core Functionality Complete', dueDate: '2025-01-15', status: 'overdue', paymentAmount: 152000, paymentReceived: false },
  { id: 'ms15', projectId: 'p4', name: 'UAT Sign-off', dueDate: '2025-03-31', status: 'pending', paymentAmount: 76000, paymentReceived: false },
  { id: 'ms16', projectId: 'p4', name: 'Go-Live', dueDate: '2025-04-30', status: 'pending', paymentAmount: 76000, paymentReceived: false },
  
  // Project 5 - Mobile App Development
  { id: 'ms17', projectId: 'p5', name: 'Design Approval', dueDate: '2024-10-31', completedDate: '2024-10-28', status: 'completed', paymentAmount: 110000, paymentReceived: true },
  { id: 'ms18', projectId: 'p5', name: 'Alpha Release', dueDate: '2024-12-31', completedDate: '2025-01-05', status: 'completed', paymentAmount: 165000, paymentReceived: false },
  { id: 'ms19', projectId: 'p5', name: 'Beta Release', dueDate: '2025-02-15', status: 'pending', paymentAmount: 165000, paymentReceived: false },
  { id: 'ms20', projectId: 'p5', name: 'App Store Launch', dueDate: '2025-02-28', status: 'pending', paymentAmount: 110000, paymentReceived: false },
  
  // Project 7 - ERP Implementation (Red - multiple overdue milestones)
  { id: 'ms21', projectId: 'p7', name: 'Requirements Finalized', dueDate: '2024-06-30', completedDate: '2024-07-15', status: 'completed', paymentAmount: 150000, paymentReceived: true },
  { id: 'ms22', projectId: 'p7', name: 'Configuration Complete', dueDate: '2024-09-30', completedDate: '2024-10-20', status: 'completed', paymentAmount: 187500, paymentReceived: true },
  { id: 'ms23', projectId: 'p7', name: 'Data Migration Complete', dueDate: '2024-12-15', status: 'overdue', paymentAmount: 187500, paymentReceived: false },
  { id: 'ms24', projectId: 'p7', name: 'UAT Sign-off', dueDate: '2025-01-10', status: 'overdue', paymentAmount: 112500, paymentReceived: false },
  { id: 'ms25', projectId: 'p7', name: 'Go-Live', dueDate: '2025-01-31', status: 'pending', paymentAmount: 112500, paymentReceived: false },
];

// Mock time entries
export const mockTimeEntries = [
  { id: 't1', projectId: 'p1', employeeId: 'e4', date: '2025-01-13', hours: 8, billable: true, description: 'Frontend development', approved: true },
  { id: 't2', projectId: 'p1', employeeId: 'e4', date: '2025-01-14', hours: 7.5, billable: true, description: 'API integration', approved: true },
  { id: 't3', projectId: 'p2', employeeId: 'e2', date: '2025-01-13', hours: 6, billable: true, description: 'Client meeting and planning', approved: true },
  { id: 't4', projectId: 'p5', employeeId: 'e7', date: '2025-01-14', hours: 8, billable: true, description: 'UI design mockups', approved: false },
  { id: 't5', projectId: 'p8', employeeId: 'e11', date: '2025-01-15', hours: 5, billable: true, description: 'Testing and QA', approved: false },
];

// Mock expenses
export const mockExpenses = [
  { id: 'ex1', projectId: 'p1', employeeId: 'e1', date: '2025-01-10', amount: 1200, category: 'Travel', description: 'Client site visit - flights', billable: true, approved: true },
  { id: 'ex2', projectId: 'p1', employeeId: 'e1', date: '2025-01-10', amount: 450, category: 'Accommodation', description: 'Hotel - 3 nights', billable: true, approved: true },
  { id: 'ex3', projectId: 'p2', employeeId: 'e2', date: '2025-01-12', amount: 85, category: 'Software', description: 'Development tools license', billable: true, approved: true },
  { id: 'ex4', projectId: 'p5', employeeId: 'e7', date: '2025-01-14', amount: 299, category: 'Software', description: 'Design software subscription', billable: true, approved: false },
  { id: 'ex5', projectId: 'p4', employeeId: 'e6', date: '2025-01-15', amount: 150, category: 'Software', description: 'Development tools', billable: true, approved: false },
];
